import type { RuleCondition } from "@sauron/contracts"
import type { Registry } from "@/api.js"
import { money } from "@/lib/money.js"

export type ConditionVocabulary = Registry["conditions"]

/** What an empty set reads as. A rule with no conditions runs for every request. */
export const NO_CONDITIONS = "bez warunków"

/**
 * The vocabulary, or an empty one when the API predates it.
 *
 * A registry served by an older build carries no `conditions` key at all, and
 * reaching into it would white-screen the configuration page — a build mismatch
 * turning into a crash on the screen somebody opens to fix things. Normalised
 * once here, it becomes a sentence in the editor instead.
 */
export function vocabularyOf(registry: Pick<Registry, "conditions">): ConditionVocabulary {
  return (
    registry.conditions ?? { fields: [], operators: { money: [], text: [] }, missingPolicies: [] }
  )
}

/**
 * One line of Polish for a rule's conditions, used by the publish diff and by
 * the rule row.
 *
 * **Written from the served vocabulary, not from a table in here.** A field
 * added to `context` for a second question arrives with its own label and its
 * operators, so this keeps reading correctly without an edit — the same
 * argument the manifests make everywhere else on this screen.
 */
export function describeConditions(
  conditions: readonly RuleCondition[],
  vocabulary: ConditionVocabulary,
): string {
  if (conditions.length === 0) return NO_CONDITIONS
  return conditions.map((condition) => describeCondition(condition, vocabulary)).join(" i ")
}

export function describeCondition(
  condition: RuleCondition,
  vocabulary: ConditionVocabulary,
): string {
  const spec = vocabulary.fields.find((field) => field.field === condition.field)
  const label = spec?.label ?? condition.field
  const operator =
    (spec === undefined
      ? undefined
      : vocabulary.operators[spec.kind]?.find((option) => option.value === condition.operator)
          ?.label) ?? condition.operator

  // Switched rather than chained, because `operator` is what discriminates the
  // union — the presence variant carries no operand at all.
  switch (condition.operator) {
    case "present":
    case "absent":
      return `${lower(label)} ${operator}`
    case "in":
    case "not_in":
      return `${lower(label)} ${operator} ${condition.value.join(", ")}${missing(condition)}`
    default:
      return `${lower(label)} ${operator} ${money(condition.value)}${missing(condition)}`
  }
}

/**
 * Only shown when it is the exception. Printing "bez tego pola: nie uruchamiaj"
 * on every line would bury the one line that says otherwise.
 */
function missing(condition: { readonly whenMissing?: "fail" | "hold" }): string {
  return condition.whenMissing === "hold" ? " (bez tego pola: uruchom)" : ""
}

function lower(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1)
}
